/*
 * WhatsApp Message Analysis — used by the CXO and Marketing panels.
 * Day-wise message counts for one message type; "Not Generated" / "Failed" open the user list.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { Copy, MessageCircle } from "lucide-react";
import {
  failedWhatsAppMessage,
  getMessageType,
  notGeneratedwhatsAppMessage,
  showMessageData,
} from "../../Action/courseAction";
import { showSuccessSnackbar } from "../../Action/snackbarAction";
import { CustomAdminTable, CustomBadge, CustomIconButton, CustomPageLayout } from "../../Component/customComponents";
import { BatchNames, buildDateFilterFields, formatDateRange, todayISO } from "../Common/transactionCommon";
import { CountLink } from "../Common/countListCommon";
import { maskMobile } from "../Common/orderAnalysisCommon"; // shared strict masking

const STAGE_TYPES = ["Extend Package", "Upgrade Package"];
const needsStage = (messageType) => STAGE_TYPES.includes(messageType?.name);
const plain = (v) => (v || v === 0 ? v : "—");
// dropdowns need an _id; message types / stages may come without one
const withIds = (list, idKey) => (list || []).map((x) => ({ ...x, _id: x?._id ?? x?.[idKey] ?? x?.name }));

function WhatsappMessageAnalysis() {
  const dispatch = useDispatch();
  const [messageList, setMessageList] = useState([]);
  const [stageList, setStageList] = useState([]);
  const [filters, setFilters] = useState(() => ({ startDate: todayISO(), endDate: todayISO(), messageType: "", stage: "" }));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // drill-down: users whose message was not generated / failed on one day
  const [detail, setDetail] = useState(null); // { kind: "failed" | "notGenerated", date }
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    dispatch(
      getMessageType(
        (resp) => {
          const list = Array.isArray(resp) ? resp : [];
          // same stage lookup as before
          const stages = list.find((m) => m?.type === "extend-package" || m?.type === "Upgrade Package")?.stages || [];
          setStageList(withIds(stages, "name"));
          setMessageList(withIds(list, "type"));
        },
        () => {},
      ),
    );
  }, []);

  /** same request as before: { startDate, endDate, type, stage? } */
  const fetchRows = (f) => {
    if (!f.messageType) return;
    setLoading(true);
    dispatch(
      showMessageData(
        { startDate: f.startDate, endDate: f.endDate, type: f.messageType?.type, ...(needsStage(f.messageType) && { stage: f.stage?.name }) },
        (res) => {
          setRows(Array.isArray(res?.data) ? res.data : []);
          setLoading(false);
        },
        () => {
          setRows([]);
          setLoading(false);
        },
      ),
    );
  };

  const openDetail = (date, kind) => {
    setDetail({ kind, date });
    setDetailRows([]);
    setPage(1);
    setDetailLoading(true);
    const action = kind === "failed" ? failedWhatsAppMessage : notGeneratedwhatsAppMessage;
    dispatch(
      action(
        { startDate: date, endDate: date, type: filters.messageType?.type }, // same as before
        (res) => {
          setDetailRows(Array.isArray(res?.data) ? res.data : []);
          setDetailLoading(false);
        },
        () => setDetailLoading(false),
      ),
    );
  };

  const filterFields = useMemo(
    () => [
      ...buildDateFilterFields(),
      { key: "messageType", label: "Message Type", type: "dropdown", options: messageList, isRequired: true, clears: ["stage"] },
      {
        key: "stage",
        label: "Stage",
        type: "dropdown",
        options: stageList,
        hidden: (v) => !needsStage(v.messageType),
        isRequired: true,
        validate: (value, v) => (needsStage(v.messageType) && !value ? "Select a stage" : ""),
      },
    ],
    [messageList, stageList],
  );

  const totals = useMemo(
    () => rows.reduce((t, r) => ({ sent: t.sent + (Number(r?.send) || 0), failed: t.failed + (Number(r?.failed) || 0) }), { sent: 0, failed: 0 }),
    [rows],
  );

  const columns = [
    { key: "date", header: "Message Date", minWidth: 120, render: (v) => (v ? <strong>{moment.utc(v).format("DD/MM/YYYY")}</strong> : "—") },
    { key: "notGenerated", header: "Not Generated", align: "right", render: (v, r) => <CountLink value={v} onClick={() => openDetail(r.date, "notGenerated")} title="View users whose message was not generated" tone="warning" /> },
    { key: "totalRegistrations", header: "Lead Count", align: "right", render: plain },
    { key: "read", header: "Read", align: "right", render: plain },
    { key: "delivered", header: "Delivered", align: "right", render: plain },
    { key: "totalUsers", header: "Total Messages", align: "right", render: plain },
    { key: "send", header: "Sent", align: "right", render: plain },
    {
      key: "failed",
      header: "Failed",
      align: "right",
      render: (v, r) => <CountLink value={v} onClick={() => openDetail(r.date, "failed")} title="View failed users" tone="danger" />,
    },
    { key: "sendRatio", header: "Sent Ratio", align: "right", render: plain },
    { key: "failureRatio", header: "Failed Ratio", align: "right", render: plain },
  ];

  const copyMobile = (m) => {
    navigator.clipboard?.writeText(String(m));
    dispatch(showSuccessSnackbar("Number copied!"));
  };
  const detailColumns = [
    { key: "firstName", header: "First Name", minWidth: 120, render: (v) => <strong>{v || "—"}</strong> },
    { key: "lastName", header: "Last Name", minWidth: 110 },
    { key: "examName", header: "Exam Name", minWidth: 130 },
    { key: "baseCourseName", header: "Exam Stream", minWidth: 140, render: (v) => <BatchNames names={v} /> },
    {
      key: "mobileNo",
      header: "Mobile No",
      minWidth: 140,
      render: (v) =>
        v ? (
          <span className="d-inline-flex align-items-center gap-1">
            {maskMobile(v)}
            <CustomIconButton icon={<Copy size={12} />} size="sm" title="Copy number" onClick={() => copyMobile(v)} />
          </span>
        ) : (
          "—"
        ),
    },
    { key: "source", header: "Source", minWidth: 120 },
    { key: "type", header: "Type", minWidth: 110 },
    { key: "reason", header: "Reason", minWidth: 200, wrap: true, render: (v) => (v ? <span className="text-danger">{v}</span> : "—") },
  ];

  const typeText = filters.messageType ? `${filters.messageType.name}${needsStage(filters.messageType) && filters.stage ? ` · ${filters.stage.name}` : ""}` : "";

  return (
    <CustomPageLayout title="WHATSAPP MESSAGE ANALYSIS" isGoBack={Boolean(detail)} onBack={() => setDetail(null)}>
      {detail ? (
        <CustomAdminTable
          key="detail"
          id="whatsapp-message-users"
          title={
            <span>
              {detail.kind === "failed" ? "Failed Message List" : "Not Generated Message List"} ({detailRows.length})
              <span className="d-block small fw-normal text-muted">
                {moment.utc(detail.date).format("DD/MM/YYYY")} · {typeText}
              </span>
            </span>
          }
          columns={detailColumns}
          data={detailRows.slice((page - 1) * pageSize, page * pageSize)}
          loading={detailLoading}
          rowKey={(r, i) => r?._id || `${r?.mobileNo}-${i}`}
          enableSerialNumber
          serialHeader="S.No"
          pagination={{
            page,
            pageSize,
            total: detailRows.length,
            onPageChange: setPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setPage(1);
            },
          }}
          emptyTitle="No users"
          maxHeight="83vh"
        />
      ) : (
        <CustomAdminTable
          key="days"
          id="whatsapp-message-analysis"
          title={
            <span>
              Message Analysis ({rows.length} days)
              <span className="d-block small fw-normal text-muted">
                {formatDateRange(filters.startDate, filters.endDate)}
                {typeText && ` · ${typeText}`}
              </span>
            </span>
          }
          columns={columns}
          data={rows}
          loading={loading}
          rowKey={(r, i) => r?.date || i}
          enableSerialNumber
          serialHeader="S.No"
          toolbarActions={
            rows.length > 0 && (
              <>
                <CustomBadge tone="success" icon={<MessageCircle size={12} />}>
                  Sent: {totals.sent.toLocaleString("en-IN")}
                </CustomBadge>
                <CustomBadge tone="danger">Failed: {totals.failed.toLocaleString("en-IN")}</CustomBadge>
              </>
            )
          }
          advancedFilters={filterFields}
          advancedFilterValues={filters}
          onApplyFilters={(values) => {
            setFilters(values);
            fetchRows(values);
          }}
          filterSubtitle="Pick the dates and a message type"
          emptyTitle={filters.messageType ? "No messages" : "Pick a message type"}
          emptyDescription={filters.messageType ? "Nothing for this period." : "Open Filters and choose a message type to see the analysis."}
          maxHeight="83vh"
        />
      )}
    </CustomPageLayout>
  );
}

export default WhatsappMessageAnalysis;
